/**
 * C-PDP-ADVANCE-01D | PDP-only compra anticipada por variante
 */
(() => {
  'use strict';

  const FORM_SELECTOR = '.product_form[data-product-form]';
  const CONTRACT_SELECTOR = '[data-chagual-advance-purchase-contract]';
  const NOTICE_SELECTOR = '[data-chagual-advance-purchase-notice]';
  const DETAIL_SELECTOR = '[data-chagual-advance-purchase-detail]';
  const RECOVERY_SELECTOR = '[data-chagual-soldout-recovery]';
  const RECOVERY_LINK_SELECTOR = '[data-chagual-soldout-whatsapp]';
  const QUANTITY_SELECTOR = '[data-chagual-purchase-quantity]';
  const BUTTONS_SELECTOR = '[data-chagual-purchase-buttons]';
  const DELIVERY_SELECTOR = '[data-p0-delivery]';
  const BUTTON_SELECTOR = '.add_to_cart[name="add"], .add_to_cart';
  const VARIANT_SELECTOR = 'select[name="id"], input[name="id"]';
  const ADVANCE_BUTTON_LABEL = 'COMPRA ANTICIPADA';
  const ADVANCE_STOCK_LABEL = 'Compra anticipada';
  const SOLD_OUT_FALLBACK = 'Agotado';
  const BLOCKED_LABEL = 'Actualmente no disponible';
  const WHATSAPP_BASE_URL = 'https://wa.me/56945121308';
  const SHIPPING_NOTE = 'No aplica despacho 24/48h';
  const SHIPPING_DISPLAY_NOTE = 'No disponible para despacho 24/48 h.';
  const ETA_PENDING = 'por confirmar';

  const defaults = {
    top_text: 'Ante la alta demanda de esta pieza, una nueva reposición ya está en camino.',
    top_text_lead: 'Ante la alta demanda de esta pieza,',
    top_text_continuation: 'una nueva reposición ya está en camino.',
  };

  const onReady = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const getText = (key, fallback) => {
    const translations = window.PXUTheme && window.PXUTheme.translation;
    return (translations && translations[key]) || fallback;
  };

  const normalizeText = (value) => {
    return String(value || '').replace(/\s+/g, ' ').trim();
  };

  const getRestockEta = (variant) => normalizeText(variant && variant.restock_fecha_eta);

  const getTopText = (variant) => {
    return normalizeText(variant && variant.top_text) || defaults.top_text;
  };

  const buildBottomTextWithEta = (etaText) => {
    const eta = normalizeText(etaText) || ETA_PENDING;
    return `Despacho estimado · ${eta}\n${SHIPPING_DISPLAY_NOTE}`;
  };

  const getBottomText = (variant) => {
    return normalizeText(variant && variant.bottom_text) || buildBottomTextWithEta(getRestockEta(variant));
  };

  const createTextSpan = (className, text) => {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
  };

  const renderTopText = (node, text) => {
    const normalized = normalizeText(text);
    node.replaceChildren();

    if (normalized !== defaults.top_text) {
      node.textContent = normalized;
      return;
    }

    node.append(
      createTextSpan('chagual-pdp-advance-editorial__lead', defaults.top_text_lead),
      document.createTextNode(' '),
      createTextSpan('chagual-pdp-advance-editorial__continuation', defaults.top_text_continuation),
    );
  };

  const renderDetailText = (node, variant) => {
    const customText = normalizeText(variant && variant.bottom_text);
    node.replaceChildren();

    if (customText) {
      node.append(createTextSpan('chagual-pdp-advance-editorial__detail-line', customText));
      return;
    }

    const etaLine = createTextSpan('chagual-pdp-advance-editorial__detail-line', 'Despacho estimado · ');
    etaLine.append(createTextSpan('chagual-pdp-advance-editorial__eta', getRestockEta(variant) || ETA_PENDING));
    node.append(
      etaLine,
      createTextSpan('chagual-pdp-advance-editorial__detail-line', SHIPPING_DISPLAY_NOTE),
    );
  };

  const parseContract = (form) => {
    if (!form) return null;
    if (form.__chagualAdvancePurchaseContract !== undefined) {
      return form.__chagualAdvancePurchaseContract;
    }

    const node = form.querySelector(CONTRACT_SELECTOR);
    if (!node) {
      form.__chagualAdvancePurchaseContract = null;
      return null;
    }

    try {
      const parsed = JSON.parse(node.textContent || '');
      if (!parsed || !Array.isArray(parsed.variants)) {
        form.__chagualAdvancePurchaseContract = null;
        return null;
      }

      const byId = {};
      parsed.variants.forEach((variant) => {
        if (variant && variant.variant_id) {
          byId[String(variant.variant_id)] = variant;
        }
      });

      form.__chagualAdvancePurchaseContract = {
        variants: byId,
      };
    } catch (error) {
      form.__chagualAdvancePurchaseContract = null;
    }

    return form.__chagualAdvancePurchaseContract;
  };

  const getVariantId = (form) => {
    const input = form && form.querySelector(VARIANT_SELECTOR);
    return input && input.value ? String(input.value) : '';
  };

  const getVariant = (form) => {
    const contract = parseContract(form);
    const variantId = getVariantId(form);
    if (!contract || !variantId) return null;
    return contract.variants[variantId] || null;
  };

  const getQuantity = (variant) => {
    const quantity = Number(variant && variant.inventory_quantity);
    return Number.isFinite(quantity) ? quantity : null;
  };

  const hasInventoryManagement = (variant) => {
    return !!(variant && variant.inventory_management);
  };

  const isManagedOutOfStock = (variant) => {
    const quantity = getQuantity(variant);
    return hasInventoryManagement(variant) && quantity !== null && quantity <= 0;
  };

  const isNormallyAvailable = (variant) => {
    if (!variant) return false;
    if (variant.inventory_policy === 'continue' && hasInventoryManagement(variant)) return true;
    if (variant.available === true) return true;

    const quantity = getQuantity(variant);
    if (hasInventoryManagement(variant)) return quantity !== null && quantity > 0;

    return false;
  };

  const qualifiesForAdvancePurchase = (variant) => {
    return !!(
      variant &&
      variant.advance_purchase_enabled === true &&
      variant.inventory_policy === 'continue' &&
      isManagedOutOfStock(variant)
    );
  };

  const shouldBlockPurchase = (variant) => {
    return !!(
      variant &&
      variant.inventory_policy === 'deny' &&
      isManagedOutOfStock(variant)
    );
  };

  const getPurchaseState = (variant) => {
    if (!variant) return 'unknown';
    if (qualifiesForAdvancePurchase(variant)) return 'active';
    if (shouldBlockPurchase(variant)) return 'blocked';
    if (isNormallyAvailable(variant)) return 'normal';
    return 'unavailable';
  };

  const getButton = (form) => form && form.querySelector(BUTTON_SELECTOR);

  const setStockLabel = (form, variant) => {
    const productRoot = form && form.closest('.product-main');
    const stockLabel = productRoot && productRoot.querySelector('[data-p0-stock]');
    if (!stockLabel || !variant) return;

    const quantity = getQuantity(variant);
    let label = variant.available === true ? 'Disponible' : SOLD_OUT_FALLBACK;

    if (shouldBlockPurchase(variant)) {
      label = BLOCKED_LABEL;
    } else if (qualifiesForAdvancePurchase(variant)) {
      label = ADVANCE_STOCK_LABEL;
    } else if (hasInventoryManagement(variant) && quantity !== null && quantity > 0 && quantity <= 3) {
      label = 'Últimas unidades';
    }

    stockLabel.textContent = label;
  };

  const setButtonText = (button, text) => {
    if (!button) return;
    const label = button.querySelector('.text');
    if (label) {
      label.textContent = text;
    } else {
      button.textContent = text;
    }
  };

  const setButtonDisabled = (form, disabled, label) => {
    const button = getButton(form);
    const buttons = form && form.querySelector('.purchase-details__buttons');
    const paymentButton = form && form.querySelector('.shopify-payment-button');
    if (!button) return;

    button.classList.toggle('disabled', disabled);
    if (disabled) {
      button.setAttribute('disabled', 'disabled');
    } else {
      button.removeAttribute('disabled');
    }

    if (buttons) buttons.classList.toggle('product-is-unavailable', disabled);
    if (paymentButton) {
      paymentButton.classList.toggle('disabled', disabled);
      paymentButton.querySelectorAll('button, input[type="button"], input[type="submit"]').forEach((control) => {
        control.disabled = disabled;
      });
    }
    if (label) setButtonText(button, label);
  };

  const setNotice = (form, variant, show) => {
    const notice = form && form.querySelector(NOTICE_SELECTOR);
    const detail = form && form.querySelector(DETAIL_SELECTOR);
    if (!notice && !detail) return;

    const topText = getTopText(variant);
    const bottomText = getBottomText(variant);

    if (notice) {
      const labelNode = notice.querySelector('[data-chagual-advance-purchase-label]');
      const textNode = notice.querySelector('[data-chagual-advance-purchase-summary-text], [data-chagual-advance-purchase-text]');
      if (labelNode) {
        if (show) {
          renderTopText(labelNode, topText);
        } else {
          labelNode.textContent = '';
        }
      }
      if (textNode) {
        textNode.textContent = show && !detail ? bottomText : '';
        textNode.hidden = !show || !!detail;
      }
      notice.hidden = !show;
    }

    if (detail) {
      const detailNode = detail.querySelector('[data-chagual-advance-purchase-detail-text]');
      if (detailNode) {
        if (show) {
          renderDetailText(detailNode, variant);
        } else {
          detailNode.textContent = '';
        }
      }
      detail.hidden = !show || !bottomText;
    }
  };

  const setProperties = (form, variant, enabled) => {
    const wrap = form && form.querySelector('[data-chagual-advance-purchase-properties]');
    if (!wrap) return;

    const values = {
      flag: 'Sí',
      eta: getRestockEta(variant),
      shipping_note: SHIPPING_NOTE,
    };

    wrap.querySelectorAll('[data-chagual-advance-purchase-property]').forEach((input) => {
      const key = input.getAttribute('data-chagual-advance-purchase-property');
      if (enabled && values[key]) {
        input.value = values[key];
        input.disabled = false;
      } else {
        input.value = '';
        input.disabled = true;
      }
    });
  };

  const getVariantTitle = (variant) => {
    const title = normalizeText(variant && variant.variant_title);
    return title && title.toLowerCase() !== 'default title' ? title : '';
  };

  const buildWhatsAppUrl = (form, variant) => {
    const productTitle = normalizeText(form && form.dataset && form.dataset.productTitle);
    const variantTitle = getVariantTitle(variant);
    const subject = variantTitle ? `${productTitle}, variante ${variantTitle}` : productTitle;
    const message = `Hola, quisiera consultar la pr\u00f3xima reposici\u00f3n de ${subject}.`;
    return `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(message)}`;
  };

  const setSoldOutRecovery = (form, variant, show) => {
    if (!form) return;

    const recovery = form.querySelector(RECOVERY_SELECTOR);
    const quantity = form.querySelector(QUANTITY_SELECTOR);
    const buttons = form.querySelector(BUTTONS_SELECTOR);
    const productRoot = form.closest('.product-main');
    const delivery = productRoot && productRoot.querySelector(DELIVERY_SELECTOR);

    if (recovery) {
      recovery.hidden = !show;
      const whatsapp = recovery.querySelector(RECOVERY_LINK_SELECTOR);
      if (whatsapp && show) whatsapp.href = buildWhatsAppUrl(form, variant);
    }
    if (quantity) quantity.hidden = show;
    if (buttons) buttons.hidden = show;
    if (delivery) delivery.hidden = show;
  };

  const applyState = (form) => {
    if (!form) return;

    const variant = getVariant(form);
    const button = getButton(form);
    const baseLabel = (button && button.getAttribute('data-label')) || '';

    form.removeAttribute('data-chagual-advance-purchase-state');
    setSoldOutRecovery(form, variant, false);

    if (!variant) {
      setNotice(form, null, false);
      setProperties(form, null, false);
      return;
    }

    setStockLabel(form, variant);

    const purchaseState = getPurchaseState(variant);

    if (purchaseState === 'active') {
      form.setAttribute('data-chagual-advance-purchase-state', 'active');
      setNotice(form, variant, true);
      setProperties(form, variant, true);
      setButtonDisabled(form, false, ADVANCE_BUTTON_LABEL);
      return;
    }

    setNotice(form, variant, false);
    setProperties(form, variant, false);

    if (purchaseState === 'blocked') {
      form.setAttribute('data-chagual-advance-purchase-state', 'blocked');
      setButtonDisabled(form, true, BLOCKED_LABEL);
      setSoldOutRecovery(form, variant, true);
      return;
    }

    if (purchaseState === 'normal') {
      setButtonDisabled(form, false, baseLabel);
      return;
    }

    setButtonDisabled(form, true, getText('sold_out_text', SOLD_OUT_FALLBACK));
  };

  const scheduleApply = (form) => {
    applyState(form);
    window.requestAnimationFrame(() => applyState(form));
    window.setTimeout(() => applyState(form), 50);
    window.setTimeout(() => applyState(form), 180);
  };

  const initForm = (form) => {
    if (!form || form.dataset.chagualAdvancePurchaseInit === '1') return;
    form.dataset.chagualAdvancePurchaseInit = '1';

    form.addEventListener('change', (event) => {
      if (event.target && event.target.matches(VARIANT_SELECTOR)) {
        scheduleApply(form);
      }
    }, true);

    form.addEventListener('submit', (event) => {
      if (form.getAttribute('data-chagual-advance-purchase-state') === 'blocked') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    const button = getButton(form);
    if (button) {
      button.addEventListener('click', (event) => {
        if (form.getAttribute('data-chagual-advance-purchase-state') === 'blocked') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
    }

    scheduleApply(form);
  };

  const initAll = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach(initForm);
  };

  const scheduleAll = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach(scheduleApply);
  };

  const wrapSelectCallback = () => {
    if (typeof window.selectCallback !== 'function' || window.selectCallback.__chagualAdvancePurchaseWrapped) {
      return;
    }

    const original = window.selectCallback;
    const wrapped = function chagualAdvancePurchaseSelectCallback(...args) {
      const result = original.apply(this, args);
      scheduleAll();
      return result;
    };

    wrapped.__chagualAdvancePurchaseWrapped = true;
    window.selectCallback = wrapped;
  };

  onReady(() => {
    initAll();
    wrapSelectCallback();
    window.setTimeout(initAll, 250);
    window.setTimeout(wrapSelectCallback, 250);
    window.setTimeout(initAll, 800);
    window.setTimeout(wrapSelectCallback, 800);
  });

  document.addEventListener('variant:change', scheduleAll);
  document.addEventListener('variant:changed', scheduleAll);
  document.addEventListener('shopify:section:load', initAll);
})();
